import { type PaletteColor, styled, type Theme } from '@mui/material';
import { EventCalendar as MuiEventCalendar, eventCalendarClasses } from '@mui/x-scheduler/event-calendar';
import { fr } from 'date-fns/locale';

import { LinearProgress } from '../../atoms/LinearProgress';
import {
  CATEGORY_ACCENT_CLASS,
  EVENT_CALENDAR_VIEWS,
  type EventCalendarColor,
  type EventCalendarProps,
  REMINDER_EVENT_CLASS,
  useEventCalendar,
} from './useEventCalendar';

type Props = EventCalendarProps;

// Week numbers are always shown: the board plans interventions by week (S42…).
const PREFERENCES = { ampm: false, weekStartsOn: 1, showWeekNumber: true } as const;

const PREFERENCES_MENU = {
  toggleWeekendVisibility: true,
  toggleWeekNumberVisibility: false,
  toggleAmpm: false,
  toggleEmptyDaysInAgenda: true,
  toggleWeekStartsOn: false,
} as const;

const VIEW_CONFIG = {
  week: { initialScrollTime: 8 },
  day: { initialScrollTime: 8 },
} as const;

// NotificationsOutlined, drawn as a mask so the bell takes the event text color.
const REMINDER_ICON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2m6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5z'/%3E%3C/svg%3E\")";

// Filled bars (all-day, reminders): the palette color deepened just enough to carry white text
// at 4.5:1 or more. Amber, lime and grey need the most darkening, hence an explicit table.
const DEEP_SURFACES: Record<EventCalendarColor, string> = {
  red: '#d73b30',
  pink: '#d0386b',
  purple: '#ab47bc',
  indigo: '#3f51b5',
  blue: '#1a75c5',
  teal: '#00796b',
  green: '#37833a',
  lime: '#74771c',
  amber: '#946b25',
  orange: '#b54500',
  grey: '#757575',
};

// Timed blocks (week, day): a 35% tint under the palette's dark text, 4.7:1 or more on every palette.
function eventSurfaces(main: string, deep: string, theme: Theme) {
  const tint = (percent: number) => `color-mix(in srgb, ${main} ${percent}%, ${theme.palette.background.paper})`;

  return {
    '--event-surface-bold': deep,
    '--event-surface-bold-hover': `color-mix(in srgb, ${deep} 88%, ${theme.palette.common.black})`,
    '--event-on-surface-bold': theme.palette.common.white,
    '--event-surface-subtle': tint(35),
    '--event-surface-subtle-hover': tint(45),
  };
}

// A resource color mapped onto a scheduler palette.
function resourcePalette(color: PaletteColor, theme: Theme) {
  return {
    '--event-main': color.main,
    '--event-surface-accent': color.main,
    '--event-on-surface-subtle-primary': color.dark,
    '--event-on-surface-subtle-secondary': color.dark,
    ...eventSurfaces(color.main, color.main, theme),
  };
}

// Google Calendar style: an event with a custom color keeps a left accent in its category color.
// Bars get an inset stripe, separated from the surface by a hairline of paper so close hues stay
// distinct; timed blocks recolor the left stripe the scheduler already draws.
function categoryAccents(theme: Theme) {
  return Object.fromEntries(
    (Object.keys(DEEP_SURFACES) as EventCalendarColor[]).flatMap((color) => {
      const accentClass = `${CATEGORY_ACCENT_CLASS}-${color}`;

      return [
        [
          // Not on a bar continuing from an earlier week: its arrow already marks the start.
          `& .${accentClass}[data-variant="filled"]:not([data-starting-before-edge])`,
          {
            paddingLeft: theme.spacing(1.25),
            boxShadow: `inset ${theme.spacing(0.5)} 0 0 ${DEEP_SURFACES[color]}, inset calc(${theme.spacing(0.5)} + 1px) 0 0 ${theme.palette.background.paper}`,
          },
        ],
        [
          `& .${eventCalendarClasses.timeGridEvent}.${accentClass}`,
          { '--event-surface-accent': DEEP_SURFACES[color] },
        ],
      ];
    }),
  );
}

function paletteSurfaces(theme: Theme) {
  return Object.fromEntries(
    (Object.keys(DEEP_SURFACES) as EventCalendarColor[]).map((color) => [
      `& [data-palette="${color}"]`,
      eventSurfaces('var(--event-main)', DEEP_SURFACES[color], theme),
    ]),
  );
}

type OwnerState = {
  fill: boolean;
};

const REMINDER_TITLE_SELECTOR = [
  eventCalendarClasses.eventItemTitle,
  eventCalendarClasses.dayGridEventTitle,
  eventCalendarClasses.timeGridEventTitle,
]
  .map((titleClass) => `& .${REMINDER_EVENT_CLASS} .${titleClass}::before`)
  .join(', ');

// FilterListOutlined (FilterIcon): on phones the compact menu holds the views and the category filters,
// and its default hamburger would duplicate the app navigation button right above it.
const FILTER_ICON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M10 18h4v-2h-4zM3 6v2h18V6zm3 7h12v-2H6z'/%3E%3C/svg%3E\")";

const Root = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  height: `calc(100dvh - ${theme.spacing(22)})`,
  minHeight: 480,
  [theme.breakpoints.up('sm')]: ownerState.fill
    ? { flex: 1, height: 'auto', minHeight: 560 }
    : { minHeight: 640 },
  [`& .${eventCalendarClasses.root}`]: {
    flex: 1,
    minHeight: 0,
    fontFamily: theme.typography.fontFamily,
  },
  // Rendez-vous and reminders wear their design system resource colors.
  ...paletteSurfaces(theme),
  '& [data-palette="teal"]': resourcePalette(theme.palette.resourcesColors.rendezVous, theme),
  '& [data-palette="orange"]': resourcePalette(theme.palette.resourcesColors.rappel, theme),
  ...categoryAccents(theme),
  // The period label stays visible on phones, where the toolbar is compact.
  [`& .${eventCalendarClasses.headerToolbarLeftElement} > p[data-expanded-only]`]: {
    display: 'block',
    ...theme.typography.h6,
    '&::first-letter': {
      textTransform: 'uppercase',
    },
  },
  // Titles in semibold: event text is 12px, a regular weight thins out on colored surfaces.
  [`& .${eventCalendarClasses.timeGridEventTitle}, & .${eventCalendarClasses.dayGridEventTitle}`]: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  [`& .${eventCalendarClasses.timeGridEventTime}`]: {
    color: 'var(--event-on-surface-subtle-primary)',
  },
  [`& .${eventCalendarClasses.headerToolbarWeekNumber}`]: {
    marginLeft: theme.spacing(1),
  },
  [`& .${eventCalendarClasses.headerToolbarCompactMenuButton}`]: {
    '& svg': {
      display: 'none',
    },
    '&::before': {
      content: '""',
      width: 24,
      height: 24,
      backgroundColor: 'currentColor',
      mask: `${FILTER_ICON} center / contain no-repeat`,
    },
  },
  [theme.breakpoints.down('sm')]: {
    // Phone agenda: a narrow date column so event titles get the width.
    [`& .${eventCalendarClasses.agendaViewRow}`]: {
      gridTemplateColumns: `${theme.spacing(11)} 1fr`,
    },
    [`& .${eventCalendarClasses.agendaViewDayHeaderCell}`]: {
      flexDirection: 'column',
      gap: 0,
      padding: theme.spacing(1.5, 1),
    },
    [`& .${eventCalendarClasses.agendaViewYearAndMonthLabel}`]: {
      display: 'none',
    },
    [`& .${eventCalendarClasses.agendaView} .${eventCalendarClasses.eventItemLinesClamp}`]: {
      WebkitLineClamp: 3,
    },
    [`& .${eventCalendarClasses.agendaView} .${eventCalendarClasses.eventItemCardContent}`]: {
      height: 'auto',
      lineHeight: 1.4,
    },
    [`& .${eventCalendarClasses.agendaView} .${eventCalendarClasses.eventItemTime}`]: {
      display: 'block',
      width: 'auto',
    },
    [`& .${eventCalendarClasses.agendaView} .${eventCalendarClasses.eventItemTitle}`]: {
      ...theme.typography.body2,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.text.primary,
    },
  },
  // A reminder is told apart by its bell, not only by its color (The Color-With-Label Rule).
  [REMINDER_TITLE_SELECTOR]: {
    content: '""',
    display: 'inline-block',
    width: '1.1em',
    height: '1.1em',
    marginRight: theme.spacing(0.5),
    verticalAlign: '-0.2em',
    backgroundColor: 'currentColor',
    mask: `${REMINDER_ICON} center / contain no-repeat`,
  },
}));

const Banner = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginBottom: theme.spacing(2),
}));

const Frame = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
});

const Progress = styled('div')({
  position: 'absolute',
  insetInline: 0,
  top: 0,
  zIndex: 1,
});

/**
 * Read-only planning of the space (month, week, day, agenda), built on MUI X Scheduler.
 * Categories give each kind of event its color, its legend entry and a visibility filter;
 * activating an event hands it to the consumer (`onEventClick`) to show its own detail.
 */
export function EventCalendar(props: Props) {
  const hook = useEventCalendar(props);

  return (
    <Root ownerState={{ fill: props.fill ?? false }} data-testid={props['data-testid']}>
      {props.banner ? <Banner>{props.banner}</Banner> : null}
      <Frame>
        {props.loading ? (
          <Progress>
            <LinearProgress aria-label="Chargement des événements" />
          </Progress>
        ) : null}
        <MuiEventCalendar
          aria-label={props['aria-label'] ?? 'Calendrier'}
          events={hook.events}
          resources={hook.resources}
          views={EVENT_CALENDAR_VIEWS}
          view={props.view}
          onViewChange={hook.handleViewChange}
          visibleDate={props.visibleDate}
          onVisibleDateChange={hook.handleVisibleDateChange}
          onEventEditingStart={hook.handleEventEditingStart}
          readOnly
          eventCreation={false}
          areEventsDraggable={false}
          areEventsResizable={false}
          dateLocale={fr}
          localeText={hook.localeText}
          defaultPreferences={PREFERENCES}
          preferencesMenuConfig={PREFERENCES_MENU}
          viewConfig={VIEW_CONFIG}
        />
      </Frame>
    </Root>
  );
}

export type { EventCalendarProps };
